import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  ArrowRight,
  Loader2,
  Plus,
  Trash2,
  Edit,
  Save,
  FileText,
  Layers,
  Upload,
  X,
  Download,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import ExamImporter from '../components/ExamImporter';
import RichTextEditor, { renderSafeHtml, stripHtml } from '../components/RichTextEditor';

const DIFFICULTY_LABELS = { easy: 'سهلة', medium: 'متوسطة', hard: 'صعبة' };
const DIFFICULTY_COLORS = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  hard: 'bg-red-100 text-red-700',
};

const emptyQuestion = () => ({
  question_type: 'multiple_choice',
  text: '',
  options: [
    { id: `opt-${Date.now()}-1`, text: '' },
    { id: `opt-${Date.now()}-2`, text: '' },
  ],
  correct_answer: '',
  points: 1,
  difficulty: 'medium',
});

const TopicEditor = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { token, isTeacher } = useAuth();

  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);
  const [editing, setEditing] = useState(null); // question being edited
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newQuestion, setNewQuestion] = useState(emptyQuestion());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [importerOpen, setImporterOpen] = useState(false);
  // Import from another topic in the bank
  const [bankImportOpen, setBankImportOpen] = useState(false);
  const [bankTopics, setBankTopics] = useState([]);
  const [bankLoadingTopics, setBankLoadingTopics] = useState(false);
  const [bankSourceTopic, setBankSourceTopic] = useState(null);
  const [bankSourceQuestions, setBankSourceQuestions] = useState([]);
  const [bankLoadingQuestions, setBankLoadingQuestions] = useState(false);
  const [bankSelectedIds, setBankSelectedIds] = useState({});
  const [bankImporting, setBankImporting] = useState(false);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isTeacher) { navigate('/teacher/login'); return; }
    fetchTopic();
  }, [topicId, isTeacher]);

  const fetchTopic = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/topics/${topicId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setTopic(data);
        // A bank file only edits its identity + active toggle. The exam config
        // (grade/section/distribution/count/time/price/tries) lives on the FOLDER.
        setSettingsForm({
          title: data.title,
          description: data.description || '',
          grade: data.grade,             // read-only here (set on the folder)
          section: data.section || '',   // read-only here (set on the folder)
          is_active: data.is_active,
        });
      } else {
        toast.error('فشل تحميل الموضوع');
        navigate('/teacher/dashboard');
      }
    } catch {
      toast.error('حدث خطأ أثناء التحميل');
    }
    setLoading(false);
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const questionMatchesSearch = (q) => {
    if (!normalizedSearch) return true;
    const haystack = [
      stripHtml(q.text || ''),
      ...(q.options || []).map(o => stripHtml(o.text || '')),
      typeof q.correct_answer === 'string' ? q.correct_answer : '',
    ].join(' ').toLowerCase();
    return haystack.includes(normalizedSearch);
  };

  const filteredQuestions = (topic?.questions || []).filter(q =>
    (filterDifficulty === 'all' || q.difficulty === filterDifficulty) &&
    questionMatchesSearch(q)
  );

  const handleExportDocx = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterDifficulty !== 'all') params.set('difficulty', filterDifficulty);
      // If a search is active, export ONLY the visible filtered results
      if (normalizedSearch) {
        const ids = filteredQuestions.map(q => q.id).join(',');
        if (ids) params.set('ids', ids);
      }
      const qs = params.toString();
      const url = `${API_URL}/api/topics/${topicId}/questions/export/docx${qs ? `?${qs}` : ''}`;
      const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'تعذّر تصدير الأسئلة');
        return;
      }
      const blob = await r.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      const safeTitle = (topic?.title || 'بنك_الأسئلة').replace(/[\\/:*?"<>|]/g, '_');
      a.download = `${safeTitle}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dl);
      toast.success('تم تنزيل ملف Word');
    } catch {
      toast.error('حدث خطأ أثناء التصدير');
    }
    setExporting(false);
  };

  const validateQuestion = (q) => {
    if (!q.text || !q.text.trim()) return 'نص السؤال مطلوب';
    if (q.question_type === 'multiple_choice') {
      const opts = (q.options || []).filter(o => o.text && o.text.trim());
      if (opts.length < 2) return 'أضف خيارين على الأقل';
      if (!q.correct_answer) return 'اختر الإجابة الصحيحة';
      if (!opts.find(o => o.id === q.correct_answer)) return 'الإجابة الصحيحة يجب أن تكون من الخيارات';
    }
    if (q.question_type === 'true_false') {
      if (!['true', 'false'].includes(q.correct_answer)) return 'حدد الإجابة الصحيحة';
    }
    if (q.question_type === 'short_answer' && !q.correct_answer) return 'أدخل الإجابة الصحيحة';
    return null;
  };

  const handleAddQuestion = async () => {
    const err = validateQuestion(newQuestion);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        question_type: newQuestion.question_type,
        text: newQuestion.text.trim(),
        options: newQuestion.question_type === 'multiple_choice'
          ? (newQuestion.options || []).filter(o => o.text && o.text.trim())
          : null,
        correct_answer: newQuestion.correct_answer,
        points: parseInt(newQuestion.points, 10) || 1,
        difficulty: newQuestion.difficulty,
      };
      const r = await fetch(`${API_URL}/api/topics/${topicId}/questions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        const data = await r.json();
        setTopic(data);
        toast.success('تمت إضافة السؤال');
        setNewQuestion(emptyQuestion());
        setAddDialogOpen(false);
      } else {
        const e = await r.json();
        toast.error(e.detail || 'فشل إضافة السؤال');
      }
    } catch {
      toast.error('حدث خطأ');
    }
    setSaving(false);
  };

  const handleUpdateQuestion = async () => {
    if (!editing) return;
    const err = validateQuestion(editing);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        question_type: editing.question_type,
        text: editing.text.trim(),
        options: editing.question_type === 'multiple_choice'
          ? (editing.options || []).filter(o => o.text && o.text.trim())
          : null,
        correct_answer: editing.correct_answer,
        points: parseInt(editing.points, 10) || 1,
        difficulty: editing.difficulty,
      };
      const r = await fetch(`${API_URL}/api/topics/${topicId}/questions/${editing.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        const data = await r.json();
        setTopic(data);
        toast.success('تم التحديث');
        setEditing(null);
      } else {
        toast.error('فشل التحديث');
      }
    } catch {
      toast.error('حدث خطأ');
    }
    setSaving(false);
  };

  const handleDeleteQuestion = async (qid) => {
    if (!window.confirm('حذف هذا السؤال؟')) return;
    try {
      const r = await fetch(`${API_URL}/api/topics/${topicId}/questions/${qid}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setTopic(data);
        toast.success('تم الحذف');
      }
    } catch {
      toast.error('حدث خطأ');
    }
  };

  const handleBulkImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(bulkText);
      if (!Array.isArray(parsed)) throw new Error('يجب أن يكون مصفوفة');
    } catch (e) {
      toast.error(`JSON غير صالح: ${e.message}`);
      return;
    }
    // Normalize options for multiple_choice
    const normalized = parsed.map((q, idx) => {
      const base = {
        question_type: q.question_type || 'multiple_choice',
        text: q.text || '',
        points: q.points || 1,
        difficulty: q.difficulty || 'medium',
      };
      if (base.question_type === 'multiple_choice') {
        const opts = (q.options || []).map((o, i) => {
          if (typeof o === 'string') {
            return { id: `bulk-${idx}-${i}`, text: o };
          }
          return { id: o.id || `bulk-${idx}-${i}`, text: o.text || '' };
        });
        base.options = opts;
        // If correct_answer is a string and matches one of options, find its id
        if (q.correct_answer) {
          const match = opts.find(o => o.text === q.correct_answer || o.id === q.correct_answer);
          base.correct_answer = match ? match.id : q.correct_answer;
        }
      } else {
        base.correct_answer = q.correct_answer || '';
      }
      return base;
    });

    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/topics/${topicId}/questions/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ questions: normalized }),
      });
      if (r.ok) {
        const data = await r.json();
        setTopic(data);
        toast.success(`تمت إضافة ${normalized.length} سؤال`);
        setBulkDialogOpen(false);
        setBulkText('');
      } else {
        const e = await r.json();
        toast.error(e.detail || 'فشل الإستيراد');
      }
    } catch {
      toast.error('حدث خطأ');
    }
    setSaving(false);
  };

  // ===== Import from another topic =====
  const openBankImport = async () => {
    setBankImportOpen(true);
    setBankSourceTopic(null);
    setBankSourceQuestions([]);
    setBankSelectedIds({});
    setBankLoadingTopics(true);
    try {
      const r = await fetch(`${API_URL}/api/topics`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        // Exclude the current topic from the picker (no copy-onto-self)
        setBankTopics((Array.isArray(data) ? data : []).filter(t => t.id !== topicId));
      } else {
        toast.error('تعذّر تحميل قائمة المواضيع');
      }
    } catch {
      toast.error('حدث خطأ أثناء تحميل المواضيع');
    }
    setBankLoadingTopics(false);
  };

  const selectBankSourceTopic = async (t) => {
    setBankSourceTopic(t);
    setBankSelectedIds({});
    setBankSourceQuestions([]);
    setBankLoadingQuestions(true);
    try {
      const r = await fetch(`${API_URL}/api/topics/${t.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setBankSourceQuestions(data.questions || []);
      } else {
        toast.error('تعذّر تحميل الأسئلة');
      }
    } catch {
      toast.error('حدث خطأ أثناء تحميل الأسئلة');
    }
    setBankLoadingQuestions(false);
  };

  const toggleAllBank = () => {
    if (bankSourceQuestions.every(q => bankSelectedIds[q.id])) {
      setBankSelectedIds({});
    } else {
      const m = {};
      bankSourceQuestions.forEach(q => { m[q.id] = true; });
      setBankSelectedIds(m);
    }
  };

  const confirmBankImport = async () => {
    const chosen = bankSourceQuestions.filter(q => bankSelectedIds[q.id]);
    if (chosen.length === 0) { toast.error('اختر سؤالاً واحداً على الأقل'); return; }
    // Copy with fresh option ids — and REMAP correct_answer to the new id
    const copied = chosen.map(q => {
      const oldOpts = q.options || [];
      const newOpts = oldOpts.map(o => ({ id: `bank-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, text: o.text }));
      let newCorrect = q.correct_answer || '';
      if (q.question_type === 'multiple_choice') {
        const idx = oldOpts.findIndex(o => o.id === q.correct_answer);
        if (idx >= 0 && newOpts[idx]) newCorrect = newOpts[idx].id;
      }
      return {
        question_type: q.question_type,
        text: q.text,
        options: q.question_type === 'multiple_choice' ? newOpts : null,
        correct_answer: newCorrect,
        points: q.points ?? 1,
        difficulty: q.difficulty || 'medium',
      };
    });
    setBankImporting(true);
    try {
      const r = await fetch(`${API_URL}/api/topics/${topicId}/questions/bulk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions: copied }),
      });
      if (r.ok) {
        const data = await r.json();
        setTopic(data);
        toast.success(`تمت إضافة ${copied.length} سؤال من "${bankSourceTopic.title}"`);
        setBankImportOpen(false);
        setBankSourceTopic(null);
        setBankSourceQuestions([]);
        setBankSelectedIds({});
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'فشل الإستيراد');
      }
    } catch {
      toast.error('حدث خطأ أثناء الإستيراد');
    }
    setBankImporting(false);
  };

  const handleSaveSettings = async () => {
    if (!settingsForm) return;
    setSavingSettings(true);
    try {
      const r = await fetch(`${API_URL}/api/topics/${topicId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: settingsForm.title,
          description: settingsForm.description,
          is_active: settingsForm.is_active,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        setTopic(data);
        toast.success('تم حفظ الإعدادات');
      } else {
        toast.error('فشل الحفظ');
      }
    } catch {
      toast.error('حدث خطأ');
    }
    setSavingSettings(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#EF9F27]" />
      </div>
    );
  }

  if (!topic) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="bg-[#FFFFFF] border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/teacher/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
              <span className="text-xl font-bold text-[#EF9F27]">المعتمد في اللغة العربية</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/teacher/dashboard')}
          className="text-[#6B4C2A] hover:text-[#412402] mb-4"
        >
          <ArrowRight className="w-4 h-4 ml-2" />
          العودة للوحة التحكم
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#412402] flex items-center gap-2">
            <Layers className="w-6 h-6 text-[#EF9F27]" />
            {topic.title}
          </h1>
          <div className="flex flex-wrap gap-3 text-sm text-[#412402]/60 mt-1">
            <span>{topic.grade}{topic.section ? ` / ${topic.section}` : ''}</span>
            <span>•</span>
            <span>{topic.total_questions} سؤال في البنك</span>
            <span>•</span>
            <span>{topic.questions_per_test} سؤال في كل اختبار</span>
            <span>•</span>
            <span>{topic.duration_minutes} دقيقة</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left/Main: Questions list */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-[#E8DFCF]">
              <CardHeader className="border-b border-[#E8DFCF]">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg font-semibold text-[#412402]">الأسئلة</CardTitle>
                    <CardDescription className="text-[#412402]/60 mt-1">
                      سهلة: {topic.easy_questions} · متوسطة: {topic.medium_questions} · صعبة: {topic.hard_questions}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#412402]/40 pointer-events-none" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="ابحث في الأسئلة…"
                        className="pr-8 w-44"
                        data-testid="bank-search-input"
                        dir="auto"
                      />
                    </div>
                    <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">جميع الصعوبات</SelectItem>
                        <SelectItem value="easy">سهلة</SelectItem>
                        <SelectItem value="medium">متوسطة</SelectItem>
                        <SelectItem value="hard">صعبة</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={() => { setNewQuestion(emptyQuestion()); setAddDialogOpen(true); }} className="bg-amber-700 hover:bg-amber-800" data-testid="add-question-btn">
                      <Plus className="w-4 h-4 ml-1" /> سؤال
                    </Button>
                    <Button variant="outline" onClick={() => setImporterOpen(true)} className="border-emerald-500/40 text-emerald-700" data-testid="import-word-gform-btn">
                      <Upload className="w-4 h-4 ml-1" /> Word / Google Forms
                    </Button>
                    <Button variant="outline" onClick={openBankImport} className="border-amber-500/40 text-amber-700" data-testid="import-from-bank-btn">
                      <Layers className="w-4 h-4 ml-1" /> استيراد من موضوع آخر
                    </Button>
                    <Button variant="outline" onClick={() => setBulkDialogOpen(true)} data-testid="bulk-import-btn">
                      <Upload className="w-4 h-4 ml-1" /> استيراد JSON
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleExportDocx}
                      disabled={exporting || filteredQuestions.length === 0}
                      className="border-sky-500/40 text-sky-700"
                      data-testid="export-word-btn"
                    >
                      {exporting
                        ? <Loader2 className="w-4 h-4 animate-spin ml-1" />
                        : <Download className="w-4 h-4 ml-1" />}
                      تصدير إلى Word
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredQuestions.length === 0 ? (
                  <div className="py-12 text-center">
                    <FileText className="w-12 h-12 text-[#412402]/30 mx-auto mb-4" />
                    <p className="text-[#412402]/60">لا توجد أسئلة بعد</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredQuestions.map((q, idx) => (
                      <div key={q.id} className="p-4 hover:bg-[#FAFAFA]" data-testid={`question-item-${q.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs text-[#412402]/40">#{idx + 1}</span>
                              <Badge className={DIFFICULTY_COLORS[q.difficulty] || 'bg-slate-100'}>
                                {DIFFICULTY_LABELS[q.difficulty] || q.difficulty}
                              </Badge>
                              <Badge variant="outline" className="text-xs">{q.points} درجة</Badge>
                              <Badge variant="outline" className="text-xs">
                                {q.question_type === 'multiple_choice' ? 'اختيار من متعدد'
                                  : q.question_type === 'true_false' ? 'صح/خطأ'
                                  : q.question_type === 'short_answer' ? 'إجابة قصيرة'
                                  : q.question_type}
                              </Badge>
                            </div>
                            <p dir="auto" className="text-[#412402] font-medium whitespace-pre-wrap" dangerouslySetInnerHTML={renderSafeHtml(q.text)} />
                            {q.question_type === 'multiple_choice' && (
                              <ul className="mt-2 space-y-1 text-sm">
                                {(q.options || []).map(opt => (
                                  <li key={opt.id} className={`flex items-center gap-2 ${opt.id === q.correct_answer ? 'text-green-700 font-medium' : 'text-[#412402]/70'}`}>
                                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                                    <span dir="auto" dangerouslySetInnerHTML={renderSafeHtml(opt.text)} />
                                    {opt.id === q.correct_answer && <span className="text-xs">(الإجابة الصحيحة)</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {q.question_type === 'true_false' && (
                              <p className="text-sm text-green-700 mt-1">الإجابة: {q.correct_answer === 'true' ? 'صح' : 'خطأ'}</p>
                            )}
                            {q.question_type === 'short_answer' && (
                              <p className="text-sm text-green-700 mt-1">الإجابة: {q.correct_answer}</p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setEditing({ ...q })}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteQuestion(q.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Settings */}
          <div className="space-y-4">
            <Card className="border-[#E8DFCF]">
              <CardHeader className="border-b border-[#E8DFCF]">
                <CardTitle className="text-base font-semibold text-[#412402]">إعدادات الموضوع</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {settingsForm && <>
                  <div>
                    <Label>العنوان</Label>
                    <Input value={settingsForm.title} onChange={(e) => setSettingsForm({ ...settingsForm, title: e.target.value })} />
                  </div>
                  <div>
                    <Label>الوصف</Label>
                    <Textarea rows={2} value={settingsForm.description} onChange={(e) => setSettingsForm({ ...settingsForm, description: e.target.value })} />
                  </div>
                  {/* الصف/الشعبة وكل إعدادات الاختبار تُحدَّد على مستوى المجلد — للعرض فقط هنا */}
                  <div className="rounded-lg border border-[#E8DFCF] bg-[#FAF6EC] px-3 py-2 text-xs text-[#412402]/70 flex items-center justify-between">
                    <span>الصف/الشعبة:</span>
                    <span className="font-semibold text-[#412402]">
                      {settingsForm.grade || 'غير محدد'}{settingsForm.section ? ` / ${settingsForm.section}` : ''}
                    </span>
                  </div>
                  {/* All exam settings are now per-FOLDER (student buys the whole folder). */}
                  <div className="border-t pt-2">
                    <p className="text-[11px] text-[#412402]/60 bg-amber-50 border border-amber-200 rounded-lg p-2">
                      <b>الصف</b> و<b>الشعبة</b> و<b>توزيع الصعوبة</b> و<b>عدد الأسئلة</b> و<b>المدة</b> و<b>عدد المحاولات</b> و<b>سعر الفتح</b> و<b>إظهار الإجابات/الدرجة</b> تُحدَّد جميعها على مستوى <b>المجلد</b> من مستكشف بنك الأسئلة («إعدادات المجلد»). يشتري الطالب المجلد كاملاً. هذا الملف يحوي عنوانه ووصفه وأسئلته فقط.
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">مفعّل (متاح للطلاب)</Label>
                    <Switch checked={settingsForm.is_active} onCheckedChange={(v) => setSettingsForm({ ...settingsForm, is_active: v })} />
                  </div>
                  <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full bg-amber-700 hover:bg-amber-800">
                    {savingSettings ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Save className="w-4 h-4 ml-2" />}
                    حفظ الإعدادات
                  </Button>
                </>}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Add Question Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">إضافة سؤال</DialogTitle>
          </DialogHeader>
          <QuestionForm value={newQuestion} onChange={setNewQuestion} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} disabled={saving}>إلغاء</Button>
            <Button onClick={handleAddQuestion} disabled={saving} className="bg-amber-700 hover:bg-amber-800" data-testid="save-question-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              إضافة
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">تعديل السؤال</DialogTitle>
          </DialogHeader>
          {editing && <QuestionForm value={editing} onChange={setEditing} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>إلغاء</Button>
            <Button onClick={handleUpdateQuestion} disabled={saving} className="bg-amber-700 hover:bg-amber-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              حفظ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">استيراد أسئلة (JSON)</DialogTitle>
            <DialogDescription className="text-right">
              ألصق مصفوفة JSON بالأسئلة. كل سؤال يحتاج: text, question_type (multiple_choice/true_false/short_answer), difficulty (easy/medium/hard), points, correct_answer.
              لـ multiple_choice: options مصفوفة (نصوص أو {`{id, text}`}). إذا أعطيت correct_answer كنص يطابق نص خيار، سيُستخدم.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={14}
            className="font-mono text-xs"
            placeholder={`[
  {
    "text": "ما اسم الفاعل من 'كَتَبَ'؟",
    "question_type": "multiple_choice",
    "difficulty": "easy",
    "points": 1,
    "options": ["كاتب", "مكتوب", "كتاب", "كُتّاب"],
    "correct_answer": "كاتب"
  },
  {
    "text": "اسم الفاعل يصاغ من الفعل اللازم.",
    "question_type": "true_false",
    "difficulty": "medium",
    "points": 1,
    "correct_answer": "true"
  }
]`}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={saving}>إلغاء</Button>
            <Button onClick={handleBulkImport} disabled={saving || !bulkText.trim()} className="bg-amber-700 hover:bg-amber-800" data-testid="confirm-bulk-import-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Upload className="w-4 h-4 ml-2" />}
              استيراد
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from another topic in the question bank */}
      <Dialog open={bankImportOpen} onOpenChange={setBankImportOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">استيراد أسئلة من موضوع آخر</DialogTitle>
            <DialogDescription className="text-right">
              {bankSourceTopic
                ? `الموضوع المصدر: ${bankSourceTopic.title} — اختر الأسئلة لنسخها`
                : 'اختر موضوعاً من بنك الأسئلة لاستيراد أسئلته إلى هذا الموضوع'}
            </DialogDescription>
          </DialogHeader>
          {!bankSourceTopic ? (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {bankLoadingTopics ? (
                <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto" /></div>
              ) : bankTopics.length === 0 ? (
                <div className="py-10 text-center text-sm text-[#412402]/60">لا توجد مواضيع أخرى في بنك الأسئلة</div>
              ) : (
                bankTopics.map(t => (
                  <button
                    key={t.id}
                    onClick={() => selectBankSourceTopic(t)}
                    data-testid={`pick-source-topic-${t.id}`}
                    className="w-full text-right p-3 rounded-lg border border-[#E8DFCF] hover:bg-amber-50 hover:border-amber-300 transition-colors flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium text-[#412402]">{t.title}</div>
                      <div className="text-xs text-[#412402]/60 mt-0.5">{t.grade}{t.section ? ` / ${t.section}` : ''} — {t.total_questions || 0} سؤال</div>
                    </div>
                    {t.category && <Badge variant="outline" className="text-xs">{t.category}</Badge>}
                  </button>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => { setBankSourceTopic(null); setBankSourceQuestions([]); setBankSelectedIds({}); }}>
                  ← اختيار موضوع آخر
                </Button>
                {bankSourceQuestions.length > 0 && (
                  <Button variant="outline" size="sm" onClick={toggleAllBank}>
                    {bankSourceQuestions.every(q => bankSelectedIds[q.id]) ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                  </Button>
                )}
              </div>
              <div className="max-h-[50vh] overflow-y-auto space-y-2 border rounded p-2 bg-[#FAFAFA]">
                {bankLoadingQuestions ? (
                  <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto" /></div>
                ) : bankSourceQuestions.length === 0 ? (
                  <div className="py-8 text-center text-sm text-[#412402]/60">لا توجد أسئلة في هذا الموضوع</div>
                ) : (
                  bankSourceQuestions.map((q, i) => (
                    <label
                      key={q.id}
                      className={`flex items-start gap-3 p-2 rounded border cursor-pointer ${bankSelectedIds[q.id] ? 'bg-amber-50 border-amber-300' : 'bg-white border-[#E8DFCF]'}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={!!bankSelectedIds[q.id]}
                        onChange={() => setBankSelectedIds(p => ({ ...p, [q.id]: !p[q.id] }))}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#412402]/50 mb-1">#{i + 1} • {q.difficulty || 'medium'} • {q.points || 1} نقطة</div>
                        <div dir="auto" className="text-sm font-medium text-[#412402] whitespace-pre-wrap" dangerouslySetInnerHTML={renderSafeHtml(q.text)} />
                        {q.question_type === 'multiple_choice' && q.options && (
                          <ul className="mt-1 pr-4 text-xs text-[#412402]/70 list-disc">
                            {q.options.map(o => (
                              <li key={o.id} dir="auto" className={o.id === q.correct_answer ? 'text-green-700 font-semibold' : ''}
                                  dangerouslySetInnerHTML={renderSafeHtml(o.text)} />
                            ))}
                          </ul>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankImportOpen(false)} disabled={bankImporting}>إلغاء</Button>
            {bankSourceTopic && (
              <Button
                onClick={confirmBankImport}
                disabled={bankImporting || Object.values(bankSelectedIds).filter(Boolean).length === 0}
                className="bg-amber-700 hover:bg-amber-800"
                data-testid="confirm-bank-import-btn"
              >
                {bankImporting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Upload className="w-4 h-4 ml-2" />}
                إضافة المختار ({Object.values(bankSelectedIds).filter(Boolean).length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExamImporter
        open={importerOpen}
        onOpenChange={setImporterOpen}
        token={token}
        onImport={async (questions) => {
          if (!questions || questions.length === 0) return;
          setSaving(true);
          try {
            const r = await fetch(`${API_URL}/api/topics/${topicId}/questions/bulk`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ questions }),
            });
            if (r.ok) {
              const data = await r.json();
              setTopic(data);
              toast.success(`تمت إضافة ${questions.length} سؤال إلى البنك`);
            } else {
              const e = await r.json().catch(() => ({}));
              toast.error(e.detail || 'فشل الإستيراد');
            }
          } catch {
            toast.error('حدث خطأ أثناء الإستيراد');
          }
          setSaving(false);
        }}
      />
    </div>
  );
};

const QuestionForm = ({ value, onChange }) => {
  const setField = (key, val) => onChange({ ...value, [key]: val });

  const updateOption = (idx, text) => {
    const opts = [...(value.options || [])];
    opts[idx] = { ...opts[idx], text };
    onChange({ ...value, options: opts });
  };
  const addOption = () => {
    const opts = [...(value.options || []), { id: `opt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, text: '' }];
    onChange({ ...value, options: opts });
  };
  const removeOption = (idx) => {
    const opts = [...(value.options || [])];
    const removed = opts.splice(idx, 1)[0];
    const ca = value.correct_answer === removed?.id ? '' : value.correct_answer;
    onChange({ ...value, options: opts, correct_answer: ca });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>نوع السؤال</Label>
          <Select value={value.question_type} onValueChange={(v) => setField('question_type', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="multiple_choice">اختيار من متعدد</SelectItem>
              <SelectItem value="true_false">صح/خطأ</SelectItem>
              <SelectItem value="short_answer">إجابة قصيرة</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>الصعوبة</Label>
          <Select value={value.difficulty} onValueChange={(v) => setField('difficulty', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="easy">سهلة</SelectItem>
              <SelectItem value="medium">متوسطة</SelectItem>
              <SelectItem value="hard">صعبة</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>نص السؤال</Label>
        <RichTextEditor
          value={value.text}
          onChange={(v) => setField('text', v)}
          rows={3}
          testid="question-text-input"
          placeholder="اكتب نص السؤال (يدعم التنسيق: عريض / مائل / تحته خط)"
        />
      </div>
      <div>
        <Label>الدرجة</Label>
        <Input type="number" min={1} value={value.points} onChange={(e) => setField('points', e.target.value)} className="w-24" />
      </div>

      {value.question_type === 'multiple_choice' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>الخيارات</Label>
            <Button size="sm" variant="outline" onClick={addOption}><Plus className="w-3 h-3 ml-1" /> خيار</Button>
          </div>
          {(value.options || []).map((opt, idx) => (
            <div key={opt.id} className="flex items-start gap-2">
              <input
                type="radio"
                name="correct"
                checked={value.correct_answer === opt.id}
                onChange={() => setField('correct_answer', opt.id)}
                className="accent-amber-600 mt-2"
              />
              <div className="flex-1">
                <RichTextEditor
                  value={opt.text}
                  onChange={(v) => updateOption(idx, v)}
                  rows={1}
                  placeholder={`خيار ${idx + 1}`}
                />
              </div>
              <Button size="sm" variant="ghost" onClick={() => removeOption(idx)} className="text-red-500 mt-1"><X className="w-4 h-4" /></Button>
            </div>
          ))}
          <p className="text-xs text-[#412402]/50">حدد الإجابة الصحيحة بزر الراديو</p>
        </div>
      )}

      {value.question_type === 'true_false' && (
        <div>
          <Label>الإجابة الصحيحة</Label>
          <Select value={value.correct_answer || ''} onValueChange={(v) => setField('correct_answer', v)}>
            <SelectTrigger><SelectValue placeholder="اختر" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">صح</SelectItem>
              <SelectItem value="false">خطأ</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {value.question_type === 'short_answer' && (
        <div>
          <Label>الإجابة الصحيحة</Label>
          <Input value={value.correct_answer || ''} onChange={(e) => setField('correct_answer', e.target.value)} />
        </div>
      )}
    </div>
  );
};

export default TopicEditor;
